/**
 * Material Classification Manager — visual editor for the 15 material types.
 *
 * Split-panel workflow:
 *  1. Left column: **Class catalogue** (colour picker, kind = ore/waste,
 *     lithology chip mapping, drag-in / drag-out).
 *  2. Right column: **Rule builder** — first-match-wins engine identical to
 *     the backend `classify_block` semantics. Users compose predicates by
 *     grade band + lithology + domain + weathering, then re-order.
 *  3. Live rollup at the bottom driven by
 *     `POST /projects/{id}/material-classes/preview` (no persistence).
 *  4. Save + Apply persist through the pre-existing `PUT /material-classes`
 *     and `POST /material-classes/apply` endpoints.
 *  5. Import / Export JSON — round-trips the {classes, rules} shape.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Palette, Layers, Plus, Save, PlayCircle, Trash2,
  ArrowUp, ArrowDown, Eye, Download, Upload, RefreshCw,
  Wand2, Sparkles, AlertTriangle, CheckCircle2, Beaker, ScanLine,
} from "lucide-react";
import { toast } from "sonner";
import { formatTonnage } from "@/components/ssot/OreFigures";
import ClassifierExplain from "@/components/ssot/ClassifierExplain";

const KIND_OPTIONS = ["ore", "waste"];

const emptyRule = () => ({
  class_code: "",
  min_grade: "",
  max_grade: "",
  lithology_in: "",
  domain_in: "",
  weathering_in: "",
});


function parseCsvList(v) {
  if (!v) return null;
  const list = String(v).split(",").map((x) => x.trim()).filter(Boolean);
  return list.length ? list : null;
}
function stringifyCsvList(v) {
  if (!v) return "";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}


function ruleToPayload(r) {
  return {
    class_code: r.class_code,
    min_grade: r.min_grade === "" || r.min_grade == null ? null : Number(r.min_grade),
    max_grade: r.max_grade === "" || r.max_grade == null ? null : Number(r.max_grade),
    lithology_in: parseCsvList(r.lithology_in),
    domain_in:    parseCsvList(r.domain_in),
    weathering_in: parseCsvList(r.weathering_in),
  };
}
function ruleFromPayload(p) {
  return {
    class_code: p.class_code || "",
    min_grade: p.min_grade ?? "",
    max_grade: p.max_grade ?? "",
    lithology_in: stringifyCsvList(p.lithology_in),
    domain_in:    stringifyCsvList(p.domain_in),
    weathering_in: stringifyCsvList(p.weathering_in),
  };
}


export default function MaterialClassManager({ projectId, open, onOpenChange, onApplied }) {
  const [classes, setClasses] = useState([]);
  const [rules, setRules] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  // Auto Material Rule Manager (Master Spec 2026-02) — proposal state.
  const [autoProposal, setAutoProposal] = useState(null);   // { material_table, rules, cutoff_grade, ... }
  const [autoAiNarrative, setAutoAiNarrative] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [validation, setValidation] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/material-classes`);
      setClasses(r.data.classes || []);
      setDefaults(r.data.defaults || []);
      const rr = (r.data.rules || []).map(ruleFromPayload);
      setRules(rr);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load class config");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (open) { load(); setPreview(null); } }, [open, load]);

  const runPreview = useCallback(async () => {
    setBusy(true);
    try {
      const payload = {
        classes,
        rules: rules.filter((r) => r.class_code).map(ruleToPayload),
        sample_size: 25000,
      };
      const r = await api.post(`/projects/${projectId}/material-classes/preview`, payload);
      setPreview(r.data);
      toast.success("Preview computed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  }, [classes, rules, projectId]);

  const save = useCallback(async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/material-classes`, {
        project_id: projectId,
        classes,
        rules: rules.filter((r) => r.class_code).map(ruleToPayload),
      });
      toast.success("Ruleset saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  }, [classes, rules, projectId]);

  const apply = useCallback(async () => {
    // Session I-4 UX fix — Zero-rules silent fallback previously classified
    // every block via a single cutoff-grade split (hg_ore vs overburden),
    // producing dashboards that looked "populated" but were actually the
    // crudest possible resource summary. The dialog below surfaces the
    // fallback BEFORE any DB write so the user can either proceed with
    // eyes open or generate proper tiered rules first.
    const definedRules = rules.filter((r) => r.class_code);
    if (definedRules.length === 0) {
      const proceed = window.confirm(
        "No classification rules are defined.\n\n" +
        "Classification will use a basic single-cutoff split " +
        "(grade ≥ cutoff → high-grade ore, below → waste) instead of your " +
        "full 15-class system. All non-ore/overburden classes will end up empty.\n\n" +
        "For accurate tiered classification, click Cancel then " +
        "'Generate rules from drillhole data' first.\n\n" +
        "Proceed with the crude single-cutoff split anyway?"
      );
      if (!proceed) return;
    }
    setBusy(true);
    try {
      // Persist first so the apply reads exactly what the user built
      await api.put(`/projects/${projectId}/material-classes`, {
        project_id: projectId,
        classes,
        rules: definedRules.map(ruleToPayload),
      });
      const r = await api.post(`/projects/${projectId}/material-classes/apply`);
      setPreview(r.data);
      toast.success("Classification applied to the active block model");
      onApplied?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  }, [classes, rules, projectId, onApplied]);

  const exportJson = useCallback(() => {
    const payload = {
      classes,
      rules: rules.filter((r) => r.class_code).map(ruleToPayload),
      exported_at: new Date().toISOString(),
      product: "SMIN",
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `smin_material_ruleset_${projectId}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Ruleset exported");
  }, [classes, rules, projectId]);

  const importJson = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (Array.isArray(data.classes)) setClasses(data.classes);
        if (Array.isArray(data.rules)) setRules(data.rules.map(ruleFromPayload));
        toast.success("Ruleset imported — Preview or Apply to activate");
      } catch (err) {
        toast.error("Invalid ruleset JSON");
      }
    };
    reader.readAsText(file);
  }, []);

  // ---- Auto Material Rule Manager (Master Spec 2026-02) ----
  const runAutoGenerate = useCallback(async ({ withAi = false } = {}) => {
    setBusy(true);
    setAutoAiNarrative(null);
    try {
      const endpoint = withAi ? "ai-recommend" : "propose";
      const r = await api.post(`/projects/${projectId}/material-classes/${endpoint}`, {});
      setAutoProposal(r.data);
      if (withAi && r.data.ai_narrative) {
        setAutoAiNarrative(r.data.ai_narrative);
      }
      setAutoOpen(true);
      toast.success(`Analysed drillholes — ${(r.data.material_table || []).length} lithology suggestion(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-generate failed");
    } finally {
      setBusy(false);
    }
  }, [projectId]);

  const acceptAutoProposal = useCallback(() => {
    if (!autoProposal) return;
    setClasses(autoProposal.classes || []);
    setRules((autoProposal.rules || []).map(ruleFromPayload));
    setAutoOpen(false);
    toast.success("Proposal applied to editor — click Preview or Save & Apply to persist");
  }, [autoProposal]);

  const oneClickAutoApply = useCallback(async () => {
    // Session I-4 UX fix — same zero-rules guard as `apply()`, but here we
    // steer the user to the generator instead of proceeding blind, because
    // one-click flow is meant to end with a real 15-class result. If the
    // user opted in via the auto-generate path, rules WILL be populated by
    // the time this callback runs (auto-apply generates + applies in one
    // pass on the backend). This confirm only fires if a user hit the
    // button on a truly empty ruleset AND their intent to skip generation
    // is deliberate.
    const definedRules = rules.filter((r) => r.class_code);
    if (definedRules.length === 0) {
      const proceed = window.confirm(
        "No classification rules are defined yet.\n\n" +
        "One-click auto-apply calls the backend's generate-then-apply path, " +
        "which will scan your drillhole assays to build a full 15-class " +
        "ruleset from your real data. This is the recommended flow.\n\n" +
        "Proceed with generate + apply?"
      );
      if (!proceed) return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/material-classes/auto-apply`, {});
      setAutoOpen(false);
      // Reload the editor so the persisted rules show up.
      await load();
      // Convert the class_summary into a preview-shaped payload for the KPI strip.
      const cs = r.data.class_summary || {};
      setPreview({
        block_model_id: cs.block_model_id,
        blocks_sampled: cs.ore_blocks + cs.waste_blocks,
        blocks_total:   cs.ore_blocks + cs.waste_blocks,
        total_ore_tonnage:        cs.total_ore_tonnage,
        total_waste_tonnage:      cs.total_waste_tonnage,
        total_overburden_tonnage: cs.total_overburden_tonnage,
        waste_breakdown:          cs.waste_breakdown || {},
        strip_ratio:              cs.strip_ratio,
        overburden_ratio:         cs.overburden_ratio,
        avg_ore_grade:            cs.avg_ore_grade,
        contained_metal:          cs.contained_metal,
      });
      toast.success(`Auto-applied — ${r.data.rules_persisted} rules · cutoff ${r.data.cutoff_grade}`);
      onApplied?.(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail.message || "Auto-apply failed") : (detail || "Auto-apply failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [projectId, load, onApplied, rules]);

  const validateNow = useCallback(async () => {
    setBusy(true);
    try {
      const payload = {
        rules: rules.filter((r) => r.class_code).map(ruleToPayload),
      };
      const r = await api.post(`/projects/${projectId}/material-classes/validate`, payload);
      setValidation(r.data);
      if (r.data.ok) toast.success(`Validation passed — ${r.data.warnings?.length || 0} warning(s)`);
      else toast.error(`Validation failed — ${r.data.errors?.length || 0} error(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Validation failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, rules]);

  // ---- Detect classes directly from imported drillhole lithology
  //      (Feb 2026 · Session I-58) — replaces the 15 static defaults with
  //      whatever unique lithology units appear in the project's imported
  //      lithology intervals. Each detected class is pre-filled with a
  //      density (from a mining SG lookup) and a kind (ore/waste based on
  //      the associated assay grades).
  const [detectionMeta, setDetectionMeta] = useState(null);
  const [detectMode, setDetectMode] = useState("ai");  // "ai" | "manual"
  const detectFromDrillholes = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/projects/${projectId}/ai-advisor/lithology-classes`);
      const detected = (r.data.classes || []).map((c) => ({
        code: c.code,
        name: c.name,
        color: c.color,
        kind: c.kind,
        grade_tier: c.grade_tier || null,
        density: c.density,
        density_reason: c.density_reason,
        kind_reason: c.kind_reason,
        grade_tier_reason: c.grade_tier_reason,
        lithology_in: [c.name],
      }));
      if (!detected.length) {
        toast.error("No lithology intervals found — import lithology data first.");
        return;
      }
      setClasses(detected);
      setDetectionMeta({
        count: detected.length,
        project_median_grade: r.data.project_median_grade,
        detected_at: new Date().toISOString(),
      });
      toast.success(`Detected ${detected.length} lithology class(es) from imported data.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Detect failed — is lithology imported?");
    } finally {
      setBusy(false);
    }
  }, [projectId]);

  // ---- Class editing helpers
  const updateClass = (idx, patch) => {
    setClasses((cs) => cs.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };
  const addClass = () => setClasses((cs) => [...cs, {
    code: `custom_${cs.length + 1}`, name: "New Class", color: "#0EA5E9",
    kind: "ore", grade_tier: "medium", density: 2.65,
  }]);
  const removeClass = (idx) => setClasses((cs) => cs.filter((_, i) => i !== idx));
  const restoreDefaults = () => setClasses(defaults);

  // ---- Rule editing helpers
  const updateRule = (idx, patch) => {
    setRules((rr) => rr.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRule = () => setRules((rr) => [...rr, emptyRule()]);
  const removeRule = (idx) => setRules((rr) => rr.filter((_, i) => i !== idx));
  const moveRule = (idx, dir) => setRules((rr) => {
    const to = idx + dir;
    if (to < 0 || to >= rr.length) return rr;
    const next = rr.slice();
    [next[idx], next[to]] = [next[to], next[idx]];
    return next;
  });

  const classCodesInUse = useMemo(() => new Set(classes.map((c) => c.code)), [classes]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="material-class-manager"
        className="rounded-none border-border bg-card sm:max-w-[1200px] max-h-[92vh] overflow-hidden flex flex-col p-0"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-heading font-black text-xl tracking-tight flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary" />
            Material Classification Manager
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Rules evaluate top→bottom (first match wins). Preview before applying.
          </DialogDescription>
          <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="material-actions-row">
            <Button
              variant="outline" size="sm" disabled={busy}
              onClick={detectFromDrillholes}
              data-testid="material-detect-from-drillholes-btn"
              title="Scan imported lithology intervals · build a class per unit with SG density + ore/waste + grade tier auto-filled."
              className="rounded-none font-mono text-[11px] uppercase border-primary text-primary hover:bg-primary/10 h-8"
            >
              <ScanLine className="h-3.5 w-3.5 mr-1.5" />
              Detect
            </Button>
            <Button
              variant="outline" size="sm" disabled={busy}
              onClick={() => runAutoGenerate({ withAi: false })}
              data-testid="material-auto-generate-btn"
              title="Scan assay grades and propose tiered thresholds (deterministic, no AI)."
              className="rounded-none font-mono text-[11px] uppercase border-primary text-primary hover:bg-primary/10 h-8"
            >
              <Wand2 className="h-3.5 w-3.5 mr-1.5" />
              Generate rules
            </Button>
            <Button
              variant="outline" size="sm" disabled={busy}
              onClick={() => runAutoGenerate({ withAi: true })}
              data-testid="material-ai-recommend-btn"
              title="Review the generated thresholds and refine with AI reasoning."
              className="rounded-none font-mono text-[11px] uppercase h-8"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              AI refine
            </Button>
            <Button
              size="sm" disabled={busy}
              onClick={oneClickAutoApply}
              data-testid="material-auto-apply-btn"
              title="Generate, review and apply rules in one step — fastest first pass."
              className="rounded-none font-mono text-[11px] uppercase bg-primary hover:bg-primary/90 h-8"
            >
              <PlayCircle className="h-3.5 w-3.5 mr-1.5" />
              Auto-apply
            </Button>
            {detectionMeta && (
              <span className="ml-2 text-[10.5px] font-mono text-primary tracking-tight"
                title={`Median grade ${(detectionMeta.project_median_grade ?? 0).toFixed(3)}`}>
                ✓ {detectionMeta.count} class(es) from lithology
              </span>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="p-12 text-muted-foreground font-mono text-sm">Loading configuration…</div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-0 flex-1 min-h-0 overflow-hidden">
            {/* LEFT · Class catalogue */}
            <div className="border-r border-border overflow-y-auto p-4 min-w-0"
              data-testid="material-classes-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  <h3 className="font-heading font-bold text-sm">Classes ({classes.length})</h3>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={restoreDefaults}
                    data-testid="material-classes-restore"
                    className="rounded-none font-mono text-[10px] uppercase h-7">
                    <RefreshCw className="h-3 w-3 mr-1" /> Defaults
                  </Button>
                  <Button size="sm" onClick={addClass}
                    data-testid="material-classes-add"
                    className="rounded-none font-mono text-[10px] uppercase h-7">
                    <Plus className="h-3 w-3 mr-1" /> Class
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                {classes.map((c, idx) => (
                  <div key={`${c.code}-${idx}`}
                    data-testid={`material-class-row-${c.code}`}
                    className="border border-border p-2 bg-background/50">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="color"
                          aria-label={`color for ${c.code}`}
                          data-testid={`material-class-color-${c.code}`}
                          value={c.color || "#94A3B8"}
                          onChange={(e) => updateClass(idx, { color: e.target.value })}
                          className="h-8 w-8 rounded-none border border-border cursor-pointer"
                        />
                      </div>
                      <Input
                        value={c.code || ""}
                        onChange={(e) => updateClass(idx, { code: e.target.value })}
                        placeholder="code"
                        data-testid={`material-class-code-${idx}`}
                        className="rounded-none bg-input font-mono h-7 text-xs w-24"
                      />
                      <Input
                        value={c.name || ""}
                        onChange={(e) => updateClass(idx, { name: e.target.value })}
                        placeholder="Display name"
                        data-testid={`material-class-name-${idx}`}
                        className="rounded-none bg-input font-mono h-7 text-xs flex-1 min-w-0"
                      />
                      <select
                        value={c.kind || "ore"}
                        onChange={(e) => {
                          const nextKind = e.target.value;
                          updateClass(idx, {
                            kind: nextKind,
                            grade_tier: nextKind === "waste" ? null : (c.grade_tier || "medium"),
                          });
                        }}
                        data-testid={`material-class-kind-${idx}`}
                        className="rounded-none bg-input border border-border font-mono h-7 text-xs px-1 w-20"
                      >
                        {KIND_OPTIONS.map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeClass(idx)}
                        data-testid={`material-class-delete-${idx}`}
                        title="Delete class"
                        className="p-1 hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {/* Second row: density (always) + grade tier (ore only) */}
                    <div className="flex items-center gap-2 mt-1.5 pl-10 font-mono text-[10px]">
                      <label className="inline-flex items-center gap-1 text-muted-foreground uppercase tracking-widest"
                             title={c.density_reason || undefined}>
                        <Beaker className="h-3 w-3 text-primary" />
                        Density
                      </label>
                      <Input
                        type="number" step="0.05" min="0.001" max="10"
                        value={c.density ?? ""}
                        onChange={(e) => updateClass(idx, { density: parseFloat(e.target.value) || 0 })}
                        placeholder="g/cm³"
                        data-testid={`material-class-density-${idx}`}
                        className="rounded-none bg-input font-mono h-6 text-[11px] w-20"
                      />
                      <span className="text-muted-foreground">g/cm³</span>
                      {c.kind === "ore" ? (
                        <>
                          <span className="ml-3 text-muted-foreground uppercase tracking-widest">Grade</span>
                          <select
                            value={c.grade_tier || "medium"}
                            onChange={(e) => updateClass(idx, { grade_tier: e.target.value })}
                            data-testid={`material-class-tier-${idx}`}
                            className="rounded-none bg-input border border-border font-mono h-6 text-[11px] px-1"
                            title={c.grade_tier_reason || "High / Medium / Low grade tier for this ore lithology"}
                          >
                            <option value="high">high</option>
                            <option value="medium">medium</option>
                            <option value="low">low</option>
                          </select>
                        </>
                      ) : (
                        <span className="ml-3 text-muted-foreground italic">
                          Waste — no grade tier
                        </span>
                      )}
                      {(c.density_reason || c.kind_reason) && (
                        <span
                          className="ml-auto text-[9px] text-muted-foreground/70 truncate max-w-[220px]"
                          title={`Density: ${c.density_reason || "—"}\nKind: ${c.kind_reason || "—"}${c.grade_tier_reason ? `\nTier: ${c.grade_tier_reason}` : ""}`}
                        >
                          AI · why?
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT · Rule builder */}
            <div className="overflow-y-auto p-4 min-w-0" data-testid="material-rules-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <PlayCircle className="h-4 w-4 text-primary" />
                  <h3 className="font-heading font-bold text-sm">Rules ({rules.length})</h3>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                    first match wins
                  </span>
                </div>
                <Button size="sm" onClick={addRule}
                  data-testid="material-rule-add"
                  className="rounded-none font-mono text-[10px] uppercase h-7">
                  <Plus className="h-3 w-3 mr-1" /> Rule
                </Button>
              </div>

              <div className="space-y-2">
                {rules.length === 0 && (
                  <div className="border border-dashed border-primary/50 p-6 text-center text-xs text-muted-foreground font-mono space-y-2"
                    data-testid="material-rules-empty-state">
                    <div className="text-primary uppercase tracking-widest text-[10px]">No classification rules yet</div>
                    <div className="leading-relaxed">
                      Click <span className="text-primary">&ldquo;Generate rules from drillhole data&rdquo;</span> above for a
                      rule set based on your real assay data, or add rules manually below.
                    </div>
                    <div className="leading-relaxed">
                      Without rules, a basic single-cutoff split will be used
                      instead of proper grade tiers &mdash; all your non-ore /
                      overburden classes will end up empty.
                    </div>
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => runAutoGenerate({ withAi: false })}
                      data-testid="material-rules-empty-generate-cta"
                      className="mt-2 rounded-none font-mono text-[10px] uppercase border-primary text-primary hover:bg-primary/10">
                      <Wand2 className="h-3 w-3 mr-1.5" />
                      Generate now
                    </Button>
                  </div>
                )}
                {rules.map((r, idx) => {
                  const badColor = r.class_code && !classCodesInUse.has(r.class_code);
                  return (
                    <div key={idx} data-testid={`material-rule-row-${idx}`}
                      className={`border ${badColor ? "border-destructive" : "border-border"} p-2 bg-background/50 space-y-1.5`}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase text-muted-foreground w-6 text-right">
                          #{idx + 1}
                        </span>
                        <select
                          value={r.class_code}
                          onChange={(e) => updateRule(idx, { class_code: e.target.value })}
                          data-testid={`material-rule-code-${idx}`}
                          className="rounded-none bg-input border border-border font-mono h-7 text-xs px-1 flex-1"
                        >
                          <option value="">— select class —</option>
                          {classes.map((c) => {
                            const label = `${c.code} (${c.name})`;
                            return <option key={c.code} value={c.code}>{label}</option>;
                          })}
                        </select>
                        <button onClick={() => moveRule(idx, -1)} title="Move up"
                          data-testid={`material-rule-up-${idx}`}
                          className="p-1 hover:text-primary"><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => moveRule(idx, 1)} title="Move down"
                          data-testid={`material-rule-down-${idx}`}
                          className="p-1 hover:text-primary"><ArrowDown className="h-3.5 w-3.5" /></button>
                        <button onClick={() => removeRule(idx)} title="Delete rule"
                          data-testid={`material-rule-delete-${idx}`}
                          className="p-1 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="grid grid-cols-6 gap-1 pl-8 font-mono text-[10px]">
                        <label className="col-span-1">
                          <div className="text-muted-foreground uppercase">Min grade</div>
                          <Input type="number" step="0.01" value={r.min_grade}
                            data-testid={`material-rule-min-grade-${idx}`}
                            onChange={(e) => updateRule(idx, { min_grade: e.target.value })}
                            className="rounded-none bg-input h-6 text-xs font-mono" />
                        </label>
                        <label className="col-span-1">
                          <div className="text-muted-foreground uppercase">Max grade</div>
                          <Input type="number" step="0.01" value={r.max_grade}
                            data-testid={`material-rule-max-grade-${idx}`}
                            onChange={(e) => updateRule(idx, { max_grade: e.target.value })}
                            className="rounded-none bg-input h-6 text-xs font-mono" />
                        </label>
                        <label className="col-span-2">
                          <div className="text-muted-foreground uppercase">Lithology (csv)</div>
                          <Input value={r.lithology_in}
                            data-testid={`material-rule-litho-${idx}`}
                            onChange={(e) => updateRule(idx, { lithology_in: e.target.value })}
                            placeholder="Granite, Oxide, Sulphide"
                            className="rounded-none bg-input h-6 text-xs font-mono" />
                        </label>
                        <label className="col-span-1">
                          <div className="text-muted-foreground uppercase">Domain</div>
                          <Input value={r.domain_in}
                            data-testid={`material-rule-domain-${idx}`}
                            onChange={(e) => updateRule(idx, { domain_in: e.target.value })}
                            placeholder="csv"
                            className="rounded-none bg-input h-6 text-xs font-mono" />
                        </label>
                        <label className="col-span-1">
                          <div className="text-muted-foreground uppercase">Weathering</div>
                          <Input value={r.weathering_in}
                            data-testid={`material-rule-weath-${idx}`}
                            onChange={(e) => updateRule(idx, { weathering_in: e.target.value })}
                            placeholder="csv"
                            className="rounded-none bg-input h-6 text-xs font-mono" />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Live preview strip */}
        {preview && (
          <div className="border-t border-border bg-background/60 px-6 py-3 space-y-2"
            data-testid="material-preview-panel">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 font-mono text-xs">
              <PreviewKPI label="Ore tonnage"
                value={formatTonnage(preview.total_ore_tonnage, "t")}
                testId="preview-ore-tonnage" tone="ore" />
              <PreviewKPI
                label="Waste tonnage (incl. overburden)"
                value={formatTonnage(preview.total_waste_tonnage, "t")}
                testId="preview-waste-tonnage" tone="waste" />
              <PreviewKPI label="  ↳ Overburden (sub-line)"
                value={formatTonnage(preview.total_overburden_tonnage, "t")}
                testId="preview-overburden-tonnage" tone="ovb" />
              <PreviewKPI label="Strip ratio (waste/ore)"
                value={preview.strip_ratio ?? "—"} testId="preview-strip" />
              <PreviewKPI label="Avg ore grade"
                value={preview.avg_ore_grade ?? "—"} testId="preview-avg-grade" />
            </div>
            {preview.waste_breakdown && Object.keys(preview.waste_breakdown).length > 0 && (
              <div className="pt-2 border-t border-border/50" data-testid="material-waste-breakdown">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                  Waste breakdown (sub-lines)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-[11px]">
                  {Object.entries(preview.waste_breakdown).map(([code, d]) => (
                    <div key={code}
                      data-testid={`waste-sub-${code}`}
                      className="border border-border/70 px-2 py-1 flex justify-between">
                      <span className="text-muted-foreground">{d.name || code}</span>
                      <span className="text-foreground">{d.tonnage?.toLocaleString?.() ?? d.tonnage} t</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="text-[10px] text-muted-foreground italic font-mono">
              {preview.blocks_sampled?.toLocaleString()} / {preview.blocks_total?.toLocaleString()} blocks · {preview.block_model_id?.slice(-6)}
              <span className="ml-2" title="Overburden tonnage is rolled into waste in all reports and summaries.">· incl. overburden ⓘ</span>
              <span className="ml-2 text-amber-600">· preview only — click Apply to save</span>
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className="border-t border-border px-6 py-3 flex flex-wrap items-center justify-between gap-2 bg-card">
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && importJson(e.target.files[0])}
              data-testid="material-import-file"
            />
            <Button variant="outline" size="sm"
              onClick={() => fileInputRef.current?.click()}
              data-testid="material-import-json"
              className="rounded-none font-mono text-[11px] uppercase">
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Import
            </Button>
            <Button variant="outline" size="sm" onClick={exportJson}
              data-testid="material-export-json"
              className="rounded-none font-mono text-[11px] uppercase">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export JSON
            </Button>
            <Button variant="outline" size="sm" onClick={validateNow} disabled={busy}
              data-testid="material-validate-btn"
              className="rounded-none font-mono text-[11px] uppercase">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Validate
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={runPreview} disabled={busy}
              data-testid="material-preview-btn"
              className="rounded-none font-heading">
              <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
            </Button>
            <Button variant="outline" size="sm" onClick={save} disabled={busy}
              data-testid="material-save-btn"
              className="rounded-none font-heading">
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save
            </Button>
            <Button size="sm" onClick={apply} disabled={busy}
              data-testid="material-apply-btn"
              className="rounded-none font-heading bg-primary hover:bg-primary/90">
              <PlayCircle className="h-4 w-4 mr-1.5" /> Save & Apply
            </Button>
          </div>
        </div>

        {/* Validation drawer */}
        {validation && (
          <div className="border-t border-border px-6 py-3 bg-background/40 font-mono text-xs"
            data-testid="material-validation-panel">
            <div className="flex items-center justify-between mb-1">
              <div className={`smin-label ${validation.ok ? "text-emerald-500" : "text-destructive"}`}>
                {validation.ok ? "Validation passed" : "Validation failed"}
                {" · "}
                {validation.errors?.length || 0} errors · {validation.warnings?.length || 0} warnings
              </div>
              <button onClick={() => setValidation(null)}
                className="text-[10px] uppercase text-muted-foreground hover:text-foreground">
                dismiss
              </button>
            </div>
            {(validation.errors || []).map((e, i) => (
              <div key={i} className="flex items-start gap-2 text-destructive"
                data-testid={`validation-error-${e.code}`}>
                <AlertTriangle className="h-3 w-3 mt-0.5" />
                <span>{e.message}</span>
              </div>
            ))}
            {(validation.warnings || []).map((w, i) => (
              <div key={i} className="flex items-start gap-2 text-amber-500"
                data-testid={`validation-warning-${w.code}`}>
                <AlertTriangle className="h-3 w-3 mt-0.5" />
                <span>{w.message}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      {/* Auto proposal preview dialog */}
      {autoOpen && (
        <AutoProposalDialog
          proposal={autoProposal}
          aiNarrative={autoAiNarrative}
          onAccept={acceptAutoProposal}
          onCancel={() => setAutoOpen(false)}
          onOneClickApply={oneClickAutoApply}
          busy={busy}
        />
      )}
    </Dialog>
  );
}


function AutoProposalDialog({ proposal, aiNarrative, onAccept, onCancel, onOneClickApply, busy }) {
  if (!proposal) return null;
  const table = proposal.material_table || [];
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent
        data-testid="material-auto-proposal-dialog"
        className="rounded-none border-border bg-card sm:max-w-[900px] max-h-[85vh] overflow-hidden flex flex-col p-0"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-heading font-black text-lg flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Suggested material classification
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Auto-derived from the project&apos;s drillhole database (lithology + assays).
            Cut-off {proposal.cutoff_grade} · Primary element {proposal.primary_element || "—"}
            {" · "}Ore tiers: HG≥{proposal.hg_grade} · MG≥{proposal.mg_grade} · LG≥{proposal.lg_grade} · Marginal≥{proposal.marginal_grade}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto p-4 space-y-4">
          <table className="w-full font-mono text-[11px]" data-testid="material-auto-table">
            <thead>
              <tr className="text-muted-foreground uppercase text-[10px] border-b border-border">
                <th className="text-left py-1.5 px-2">Lithology</th>
                <th className="text-left py-1.5 px-2">Suggested material</th>
                <th className="text-left py-1.5 px-2">Kind</th>
                <th className="text-right py-1.5 px-2">Avg grade</th>
                <th className="text-right py-1.5 px-2">Intervals</th>
                <th className="text-right py-1.5 px-2">Length (m)</th>
                <th className="text-left py-1.5 px-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {table.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">
                  No lithologies detected — grade-only rules will be used.
                </td></tr>
              )}
              {table.map((row, i) => (
                <tr key={i} data-testid={`material-auto-row-${i}`}
                  className={`border-b border-border/40 ${row.kind === "ore" ? "bg-primary/5" : ""}`}>
                  <td className="py-1.5 px-2 font-bold">{row.lithology}</td>
                  <td className="py-1.5 px-2">
                    {row.suggested_material}
                    <ClassifierExplain
                      reason={row.reason}
                      source={row.reason?.startsWith("avg grade") ? "grade" : "ssot"}
                      className="ml-1"
                      data-testid={`material-auto-row-explain-${i}`}
                    />
                  </td>
                  <td className={`py-1.5 px-2 uppercase ${row.kind === "ore" ? "text-primary" : "text-muted-foreground"}`}>
                    {row.kind}
                  </td>
                  <td className="py-1.5 px-2 text-right">{row.avg_grade}</td>
                  <td className="py-1.5 px-2 text-right">{row.interval_count}</td>
                  <td className="py-1.5 px-2 text-right">{row.total_length}</td>
                  <td className="py-1.5 px-2 text-muted-foreground italic">{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {aiNarrative && (
            <div className="border border-primary/40 bg-primary/5 p-3 space-y-1"
              data-testid="material-auto-ai-narrative">
              <div className="smin-label text-primary flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> AI Rationale (Claude Sonnet 4.5)
              </div>
              <div className="text-xs text-foreground/90 whitespace-pre-wrap font-body">
                {aiNarrative}
              </div>
            </div>
          )}

          {(proposal.notes || []).length > 0 && (
            <div className="border border-amber-500/40 bg-amber-500/5 p-3">
              <div className="smin-label text-amber-500 mb-1">Notes</div>
              {proposal.notes.map((n, i) => (
                <div key={i} className="text-xs text-amber-500/90 mb-0.5">• {n}</div>
              ))}
            </div>
          )}

          <div className="border border-border/50 bg-background/40 p-3">
            <div className="smin-label mb-1">Detected fields</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-[11px]">
              <KVPair k="Lithology count" v={proposal.detected?.lithology_count ?? 0} />
              <KVPair k="Elements" v={(proposal.detected?.elements || []).join(", ") || "—"} />
              <KVPair k="Domains" v={(proposal.detected?.domains || []).join(", ") || "—"} />
              <KVPair k="Weathering" v={(proposal.detected?.weathering || []).join(", ") || "—"} />
              <KVPair k="Density (mean)" v={proposal.detected?.density_stats?.mean ?? "—"} />
              <KVPair k="Grade p50" v={proposal.detected?.grade_stats?.p50 ?? "—"} />
              <KVPair k="Grade p90" v={proposal.detected?.grade_stats?.p90 ?? "—"} />
              <KVPair k="Grade max" v={proposal.detected?.grade_stats?.max ?? "—"} />
            </div>
          </div>
        </div>
        <div className="border-t border-border px-6 py-3 flex items-center justify-end gap-2 bg-card">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}
            data-testid="material-auto-cancel"
            className="rounded-none font-mono text-[11px] uppercase">
            Cancel
          </Button>
          <Button variant="outline" size="sm" onClick={onAccept} disabled={busy}
            data-testid="material-auto-load"
            className="rounded-none font-mono text-[11px] uppercase">
            Load into editor
          </Button>
          <Button size="sm" onClick={onOneClickApply} disabled={busy}
            data-testid="material-auto-apply-confirm"
            className="rounded-none font-heading bg-primary hover:bg-primary/90">
            <PlayCircle className="h-4 w-4 mr-1.5" /> Apply to block model
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function KVPair({ k, v }) {
  return (
    <div>
      <div className="text-[9px] uppercase text-muted-foreground tracking-widest">{k}</div>
      <div className="text-foreground">{v}</div>
    </div>
  );
}


function PreviewKPI({ label, value, testId, tone }) {
  const toneClass = tone === "ore"   ? "text-primary"
                  : tone === "ovb"   ? "text-amber-500"
                  : tone === "waste" ? "text-muted-foreground"
                  : "text-foreground";
  return (
    <div>
      <div className="smin-label text-[9px]">{label}</div>
      <div data-testid={testId} className={`text-sm font-bold ${toneClass}`}>{value}</div>
    </div>
  );
}
